import { useState } from 'react';
import { Button } from '../ui/button';
import { 
  Database, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  RefreshCw,
  Server,
  Shield,
  Zap,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../config/api';

// PostgreSQL Migration test categories
const POSTGRES_TEST_CATEGORIES = [
  { id: 'postgres-auth', name: 'Auth & Organizations', phase: 1 },
  { id: 'postgres-services-assets', name: 'Services & Assets', phase: 2 },
  { id: 'postgres-bookings', name: 'Bookings', phase: 3 },
  { id: 'postgres-team', name: 'Team Members', phase: 4 },
  { id: 'postgres-widget', name: 'Widget (Customer)', phase: '5a' },
  { id: 'postgres-payments', name: 'Payments & Partners', phase: '5b' },
  { id: 'postgres-super-admin', name: 'Super Admin Dashboard', phase: 6 },
];

export const PostgresMigrationSection = () => {
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded

  const runPostgresTests = async () => {
    setLoading(true);
    toast.info('Running PostgreSQL migration tests...');
    
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await axios.post(
        `${API_URL}/health/test-suites/postgres-migration-full`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTestResults(response.data);
      
      const rate = response.data.summary?.success_rate || 0;
      if (rate >= 90) {
        toast.success(`PostgreSQL tests: ${rate}% passing! 🎉`);
      } else if (rate >= 70) {
        toast.warning(`PostgreSQL tests: ${rate}% passing`);
      } else {
        toast.error(`PostgreSQL tests: ${rate}% passing - needs attention`);
      }
    } catch (error) {
      console.error('PostgreSQL test error:', error);
      toast.error('Failed to run PostgreSQL tests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (rate) => {
    if (rate >= 90) return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (rate >= 70) return <CheckCircle className="w-5 h-5 text-yellow-500" />;
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getStatusColor = (rate) => {
    if (rate >= 90) return 'bg-green-100 text-green-800 border-green-200';
    if (rate >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const overallRate = testResults?.summary?.success_rate || 0;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-blue-100/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-blue-900">PostgreSQL Migration Status</h3>
              <p className="text-sm text-blue-600">
                {testResults 
                  ? `${testResults.summary?.total_passed || 0}/${testResults.summary?.total_tests || 0} tests passing`
                  : 'Run tests to check migration status'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {testResults && (
              <div className={`px-3 py-1 rounded-full text-sm font-bold border ${getStatusColor(overallRate)}`}>
                {overallRate.toFixed(1)}%
              </div>
            )}
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                runPostgresTests();
              }}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              {loading ? 'Testing...' : 'Run Tests'}
            </Button>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-blue-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-blue-600" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-blue-200 p-4 bg-white/50">
          {/* Info Banner */}
          <div className="mb-4 p-3 bg-blue-100 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <Server className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Database Backend: <span className="font-bold">PostgreSQL</span>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Tests verify all v2 API endpoints are working correctly with PostgreSQL.
                  No MongoDB dependencies in these tests.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-xs text-green-700">Encryption at Rest</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-xs text-green-700">TLS in Transit</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
              <Zap className="w-4 h-4 text-green-600" />
              <span className="text-xs text-green-700">Row-Level Security</span>
            </div>
          </div>

          {/* Phase Results */}
          {testResults?.phases ? (
            <div className="space-y-2">
              {testResults.phases.map((phase, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(phase.success_rate || 0)}
                    <div>
                      <span className="font-medium text-gray-900">{phase.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {phase.passed}/{phase.tests} tests
                      </span>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(phase.success_rate || 0)}`}>
                    {(phase.success_rate || 0).toFixed(0)}%
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Click &quot;Run Tests&quot; to check PostgreSQL migration status</p>
            </div>
          )}

          {/* Test Details (if any failures) */}
          {testResults?.phases?.some(p => p.failed > 0) && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-medium text-red-800 mb-2">Failed Tests:</h4>
              {testResults.phases.map((phase, pIndex) => (
                phase.details?.filter(d => d.status === 'FAIL').map((detail, dIndex) => (
                  <div key={`${pIndex}-${dIndex}`} className="text-xs text-red-700 py-1">
                    • {phase.name} → {detail.test}: {detail.error?.substring(0, 50)}...
                  </div>
                ))
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PostgresMigrationSection;
